import { z } from 'zod';
import type { TFunction } from 'i18next';

/**
 * Les schémas sont des fabriques et non des constantes de module : un schéma
 * construit à l'import figerait ses messages dans la langue active au
 * chargement, sans suivre `i18n.changeLanguage`.
 *
 * À consommer via `useLocalizedSchema` (src/hooks/useLocalizedSchema.ts).
 */

export const createLoginSchema = (t: TFunction) =>
  z.object({
    username: z
      .string()
      .min(1, t('validations.emailRequired'))
      .email(t('validations.emailInvalid')),
    password: z.string().min(8, t('validations.passwordMin')),
  });

export const createEmailSchema = (t: TFunction) =>
  z.object({
    email: z
      .string()
      .min(1, t('validations.emailRequired'))
      .email(t('validations.emailInvalid')),
  });

export const createPasswordSchema = (t: TFunction) =>
  z
    .object({
      password: z
        .string()
        .min(8, t('validations.passwordMin'))
        .max(14, t('validations.passwordMax'))
        .regex(/[A-Z]/, t('general.passwordMissingUppercase'))
        .regex(/[0-9]/, t('general.passwordMissingDigit'))
        .regex(/[!@#$%^&*]/, t('general.passwordMissingSpecial')),
      confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('general.passwordsDontMatch'),
      path: ['confirmPassword'],
    });

export type LoginFormData = z.infer<ReturnType<typeof createLoginSchema>>;
export type EmailFormData = z.infer<ReturnType<typeof createEmailSchema>>;
export type PasswordFormData = z.infer<ReturnType<typeof createPasswordSchema>>;
